import { formatAmount, formatAmountWithUnit } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { AdjustBalanceControl } from "../components/AdjustBalanceControl";
import { AutoClaimControl } from "../components/AutoClaimControl";
import { ErrorAlert } from "../components/ErrorAlert";
import { Fact, FactGroup, FactList, Stat } from "../components/Facts";
import { KycControl } from "../components/KycControl";
import { KycStatusBadge } from "../components/KycStatusBadge";
import { LockControl } from "../components/LockControl";
import { NotesSection } from "../components/NotesSection";
import { ShippingAddressSection } from "../components/ShippingAddressSection";
import { ShortDate } from "../components/ShortDate";
import { TrustTierBadge } from "../components/TrustTierBadge";
import { TrustTierControl } from "../components/TrustTierControl";
import { UserBattlesTab } from "../components/UserBattlesTab";
import { UserChatCard } from "../components/UserChatCard";
import { UserFraudTab } from "../components/UserFraudTab";
import { UserInventoryTab } from "../components/UserInventoryTab";
import { UserKycTab } from "../components/UserKycTab";
import { UserLedgerTab } from "../components/UserLedgerTab";
import { UserOpensTab } from "../components/UserOpensTab";
import { UserOrdersTab } from "../components/UserOrdersTab";
import { UserPurchasesTab } from "../components/UserPurchasesTab";
import { useCurrency } from "../lib/currency";
import { formatDate, formatNumber } from "../lib/format";
import { lockReasonLabel } from "../lib/lockReasons";
import { type UserDetail, useTRPC } from "../lib/trpc";
import { TRUST_TIER_LABELS } from "../lib/trustTiers";

/** Why an approved verification still left the user unverified, by `policyIssue`. */
const POLICY_TEXT: Record<string, string> = {
  duplicate:
    "Another account in this environment was verified with the same identity; this one was locked.",
  age: "The document holder is under the minimum age.",
  country: "The document country is restricted.",
};

/** The history tabs; the two screening tabs keep short labels so all eight fit on one line. */
const TABS = [
  { value: "purchases", label: "Purchases" },
  { value: "ledger", label: "Ledger" },
  { value: "opens", label: "Opens" },
  { value: "inventory", label: "Inventory" },
  { value: "orders", label: "Orders" },
  { value: "battles", label: "Battles" },
  { value: "fraud", label: "Fraud" },
  { value: "kyc", label: "Identity" },
] as const;

function count(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** "2 cards, 1 high-risk switch", or "none recorded". */
function paymentMethodsSummary(switches: UserDetail["risk"]["paymentMethodSwitches"]): string {
  if (switches.length === 0) return "none recorded";
  const highRisk = switches.filter((s) => s.highRiskSwitch).length;
  return `${count(switches.length, "card")}, ${count(highRisk, "high-risk switch", "high-risk switches")}`;
}

/** "approved · decided Sep 11, 2026, 02:20 PM", or "—" before any session. */
function latestSessionSummary(latest: UserDetail["kyc"]["latest"]): string {
  if (!latest) return "—";
  const when = latest.decidedAt
    ? `decided ${formatDate(latest.decidedAt)}`
    : `started ${formatDate(latest.createdAt)}`;
  return `${latest.status} · ${when}`;
}

/** "trusted pinned by staff@… · Sep 11, 2026, 02:20 PM"; the parts it lacks are left out. */
function overrideSummary(override: NonNullable<UserDetail["risk"]["override"]>): string {
  return [
    `${TRUST_TIER_LABELS[override.tier] ?? override.tier} pinned${override.by ? ` by ${override.by}` : ""}`,
    override.at ? formatDate(override.at) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * One end user, in full — everything the sheet on /users summarizes. A header with the standing
 * badges and the two account-wide actions, a strip of figures, then two columns: the history as
 * tabs in one card on the left, and on the right one card per subject (risk, identity, Stripe,
 * auto-claim, address, notes), each carrying the control that acts on it. Below the `xl`
 * breakpoint the subject cards drop under the history in a two-column grid. Badges appear in the
 * header once; the cards hold the detail behind them and never repeat one.
 */
export function UserPage() {
  const { userId } = useParams({ strict: false }) as { userId: string };
  const trpc = useTRPC();
  const currency = useCurrency();
  const detail = useQuery(trpc.users.get.queryOptions({ endUserId: userId }));
  const data = detail.data;

  if (detail.error) return <ErrorAlert message={detail.error.message} />;
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const { user, risk, kyc, shopper } = data;
  const showKyc = kyc.required || kyc.status !== null;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="truncate font-mono text-xl font-semibold tracking-tight">
            {user.externalId}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {user.username && (
              <Badge variant="outline" title="Username: what other shoppers see">
                @{user.username}
              </Badge>
            )}
            <span>{shopper ? shopper.email : "API-created, no storefront shopper"}</span>
            {user.autoClaim && <Badge variant="outline">auto-claim</Badge>}
            <TrustTierBadge
              tier={risk.tier}
              reason={risk.reason}
              overridden={risk.override !== null}
            />
            {showKyc && (
              <KycStatusBadge
                status={kyc.status}
                verified={kyc.verifiedAt !== null}
                policyIssue={kyc.latest?.policyIssue}
              />
            )}
            {user.lockedAt && (
              <Badge
                variant="destructive"
                title={[user.lockReason, user.lockedBy, formatDate(user.lockedAt)]
                  .filter(Boolean)
                  .join(" · ")}
              >
                locked{user.lockReasonCode ? ` · ${lockReasonLabel(user.lockReasonCode)}` : ""}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LockControl endUserId={user.id} lockedAt={user.lockedAt} />
          <AdjustBalanceControl endUserId={user.id} balanceCents={user.balanceCents} />
        </div>
      </header>

      {/* Hairlines between the cells come from the border-coloured gap showing through. */}
      <Card className="grid grid-cols-2 gap-px bg-border py-0 sm:grid-cols-4">
        <div className="bg-card px-5 py-4">
          <Stat label="Balance">{formatAmountWithUnit(user.balanceCents, currency)}</Stat>
        </div>
        <div className="bg-card px-5 py-4">
          <Stat label="Opens">{formatNumber(user.nonce)}</Stat>
        </div>
        <div className="bg-card px-5 py-4">
          <Stat label="Created">
            <ShortDate value={user.createdAt} />
          </Stat>
        </div>
        <div className="bg-card px-5 py-4">
          <Stat label="Last login">
            {shopper?.lastLoginAt ? <ShortDate value={shopper.lastLoginAt} /> : "—"}
          </Stat>
        </div>
      </Card>

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Card className="min-w-0 gap-0 pt-0">
          <Tabs defaultValue="purchases" className="gap-0">
            <TabsList variant="line" className="w-full justify-start border-b px-4">
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="flex-none">
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {/* Each panel is a CardContent so a table directly inside it bleeds to the card's
                edges, the way every other list in the dashboard does. */}
            <TabsContent value="purchases">
              <CardContent className="pt-2">
                <UserPurchasesTab rows={data.purchases} />
              </CardContent>
            </TabsContent>
            <TabsContent value="ledger">
              <CardContent className="pt-2">
                <UserLedgerTab rows={data.ledger} />
              </CardContent>
            </TabsContent>
            <TabsContent value="opens">
              <CardContent className="pt-2">
                <UserOpensTab rows={data.opens} />
              </CardContent>
            </TabsContent>
            <TabsContent value="inventory">
              <CardContent className="pt-4">
                <UserInventoryTab endUserId={user.id} />
              </CardContent>
            </TabsContent>
            <TabsContent value="orders">
              <CardContent className="pt-2">
                <UserOrdersTab endUserId={user.id} />
              </CardContent>
            </TabsContent>
            <TabsContent value="battles">
              <CardContent className="pt-2">
                <UserBattlesTab endUserId={user.id} />
              </CardContent>
            </TabsContent>
            <TabsContent value="fraud">
              <CardContent className="pt-2">
                <UserFraudTab rows={risk.assessments} />
              </CardContent>
            </TabsContent>
            <TabsContent value="kyc">
              <CardContent className="pt-2">
                <UserKycTab endUserId={user.id} />
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-1">
          <FactGroup
            title="Risk"
            action={<TrustTierControl endUserId={user.id} override={risk.override?.tier ?? null} />}
          >
            <FactList>
              <Fact label="Withdrawn today">
                {formatAmount(risk.withdrawnTodayCents, currency)} of{" "}
                {formatAmountWithUnit(risk.limits.dailyWithdrawalLimitCents, currency)}
              </Fact>
              <Fact label="Review above">
                {risk.limits.manualReviewThresholdCents === null
                  ? "never; withdrawals blocked"
                  : formatAmountWithUnit(risk.limits.manualReviewThresholdCents, currency)}
              </Fact>
              <Fact label="Clock started">
                {risk.clockStartedAt ? formatDate(risk.clockStartedAt) : "—"}
              </Fact>
              <Fact label="Payment methods">
                {paymentMethodsSummary(risk.paymentMethodSwitches)}
              </Fact>
              {risk.override && <Fact label="Override">{overrideSummary(risk.override)}</Fact>}
            </FactList>
          </FactGroup>

          {showKyc && (
            <FactGroup
              title="Identity verification"
              action={<KycControl endUserId={user.id} status={kyc.status} />}
            >
              <FactList>
                <Fact label="Required">{kyc.required ? "Yes" : "No"}</Fact>
                <Fact label="Verified">{kyc.verifiedAt ? formatDate(kyc.verifiedAt) : "—"}</Fact>
                <Fact label="Latest session">{latestSessionSummary(kyc.latest)}</Fact>
                <Fact label="Veriff said">
                  {[kyc.latest?.providerCode, kyc.latest?.providerReason]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </Fact>
                {kyc.latest?.policyIssue && (
                  <Fact label="Policy">
                    {POLICY_TEXT[kyc.latest.policyIssue] ?? kyc.latest.policyIssue}
                  </Fact>
                )}
              </FactList>
            </FactGroup>
          )}

          {shopper && (
            <FactGroup title="Stripe">
              {data.stripeCustomers.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No customer until the first checkout.
                </p>
              ) : (
                <FactList>
                  {data.stripeCustomers.map((c) => (
                    <Fact key={c.id} label="Customer" mono>
                      {c.stripeCustomerId}
                      <span className="text-muted-foreground"> · {c.stripeAccountId}</span>
                    </Fact>
                  ))}
                </FactList>
              )}
            </FactGroup>
          )}

          <FactGroup
            title="Auto-claim"
            action={<AutoClaimControl endUserId={user.id} autoClaim={user.autoClaim} />}
          >
            <p className="text-sm text-muted-foreground">
              {user.autoClaim
                ? "On: each box reveal and battle result is claimed for the user the moment it lands."
                : "Off: the user claims each box reveal and battle result before opening anything else."}
            </p>
          </FactGroup>

          <UserChatCard endUserId={user.id} username={user.username} />

          <ShippingAddressSection endUserId={user.id} address={user.shippingAddress ?? null} />
          {/* Keyed on the saved value so a save or refetch resets the draft. */}
          <NotesSection key={user.notes ?? ""} endUserId={user.id} notes={user.notes} />
        </div>
      </div>
    </div>
  );
}
